import { createFileRoute, Link } from "@tanstack/react-router";
import { BUSINESS_ID, breadcrumbSchema, serviceSchema } from "@/lib/localBusinessSchema";
import { Tv, Home, Music, Sun, Shield, Cpu, Lightbulb, Speaker, ArrowRight, Thermometer, MonitorPlay, EyeOff, Blinds, HardHat, Cable, Monitor, Mic, Building2 } from "lucide-react";
import homeTheaterImg from "@/assets/Apex_Theater.webp.asset.json";
import smartHomeImg from "@/assets/smart-home.jpg";
import control4Asset from "@/assets/control4-c4yourself.png.asset.json";
const control4Img = control4Asset.url;
import wholeHomeAudioImg from "@/assets/whole-home-audio.jpg";
import hiddenAudioImg from "@/assets/hidden-audio.jpg";
import wholeHomeVideoImg from "@/assets/whole-home-video.jpg";
import outdoorImg from "@/assets/outdoor-av.jpg";
import climateImg from "@/assets/climate-control.jpg";
import lightingImg from "@/assets/lighting.jpg";
import shadingImg from "@/assets/shading.jpg";
import securityCardImg from "@/assets/security-card-800.webp.asset.json";
import networkingImg from "@/assets/network-rack-card-800.webp.asset.json";
import conferenceImg from "@/assets/conference-av.jpg";
import commercialAvImg from "@/assets/commercial-av-denver.jpg";
import prewireImg from "@/assets/denver-new-construction-prewire.jpg.asset.json";
import retrofitImg from "@/assets/denver-retrofit-cable-fishing.webp.asset.json";
import tvMountingImg from "@/assets/tv-mounting.jpg";
import avInstallImg from "@/assets/brands-lifestyle.webp";
import voiceControlImg from "@/assets/voice-control.jpg";

export const Route = createFileRoute("/services/")({
  head: () => ({
    meta: [
      { title: "Denver AV & Smart Home Services | Apex AV" },
      { name: "description", content: "Denver AV & smart home services: home theater, automation, whole-home audio, lighting, shading, security, networking & TV mounting. Free consultation." },
      { name: "keywords", content: "AV installation services Denver, Control4 dealer Denver, Control4 installer Denver, home theater installation Denver, smart home services Denver, whole-home audio Denver, lighting control Denver, motorized shades Denver, TV mounting Denver, low voltage prewire Denver, security camera installation Denver, Wi-Fi installer Denver, conference room AV Denver, outdoor TV installation Denver, hidden speaker installation Denver, whole-home video distribution Denver, smart climate control Denver, retrofit cable fishing Denver, structured cabling Denver, home network installation Denver, residential AV integrator Denver, custom home theater design Denver, surround sound installation Denver, smart lighting Denver, home automation installer Denver" },
      { property: "og:title", content: "Denver AV & Smart Home Services | Apex AV" },
      { property: "og:description", content: "Custom home theaters, automation, audio, lighting, shading & more across the Denver Metro. Free consultation." },
      { property: "og:url", content: "https://apexav.com/services" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `https://apexav.com${homeTheaterImg.url}` },
      { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
      { name: "twitter:title", content: "Denver AV & Smart Home Services | Apex AV" },
      { name: "twitter:description", content: "Custom home theaters, automation, audio, lighting, shading & more across the Denver Metro. Free consultation." },
      { name: "twitter:image", content: `https://apexav.com${homeTheaterImg.url}` },
    ],
    links: [{ rel: "canonical", href: "https://apexav.com/services" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              name: "Denver AV & Smart Home Services",
              url: "https://apexav.com/services",
              description: "Full-service Denver AV installation, home automation, audio, lighting, shading, security, and networking.",
              breadcrumb: breadcrumbSchema([
                { name: "Home", url: "https://apexav.com/" },
                { name: "Services", url: "https://apexav.com/services" },
              ]),
              mainEntity: {
                "@type": "ItemList",
                itemListElement: services.map((s, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  name: s.title,
                  url: `https://apexav.com${s.to}`,
                })),
              },
            },
            {
              "@id": BUSINESS_ID,
              hasOfferCatalog: {
                "@type": "OfferCatalog",
                name: "Denver AV & Smart Home Services",
                itemListElement: services.map((s) => ({
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: s.title,
                    url: `https://apexav.com${s.to}`,
                  },
                })),
              },
            },
            ...services.map((s) =>
              serviceSchema({
                name: `${s.title} — Denver, CO`,
                description: s.desc,
                url: `https://apexav.com${s.to}`,
                serviceType: s.title,
              }),
            ),
          ],
        }),
      },
    ],
  }),
  component: ServicesPage,
});

const services = [
  { icon: Tv, title: "Custom Home Theater", desc: "Dedicated theater rooms designed acoustically and visually for reference-grade performance — immersive surround, 4K/8K projection, motorized screens, and acoustic treatment.", to: "/services/home-theater" as const, link: true, image: homeTheaterImg.url },
  { icon: Speaker, title: "Dolby Atmos Home Theater", desc: "Immersive speaker layouts, overhead-channel installation, subwoofer integration, and precise room calibration for dedicated theaters and media rooms.", to: "/services/dolby-atmos-home-theater" as const, link: true, image: homeTheaterImg.url },
  { icon: Home, title: "Smart Home Automation", desc: "One interface for lighting, shades, HVAC, audio, security, and access — tailored to your home.", to: "/services/smart-home" as const, link: true, image: smartHomeImg },
  { icon: Cpu, title: "Control4 Home Automation", desc: "Authorized Control4 dealer — certified Control4 installation, programming, and takeover support for lighting, audio, video, shades, climate, and security.", to: "/services/control4" as const, link: true, image: control4Img },
  { icon: Music, title: "Whole-Home Audio", desc: "Multi-room audio with hidden in-wall and in-ceiling speakers. Stream your favorite services to any zone, independently or all at once.", to: "/services/whole-home-audio" as const, link: true, image: wholeHomeAudioImg },
  { icon: EyeOff, title: "Hidden & Invisible Audio", desc: "Premium invisible-series speakers plastered behind drywall — invisible to the eye, indistinguishable to the ear.", to: "/services/hidden-audio" as const, link: true, image: hiddenAudioImg },
  { icon: MonitorPlay, title: "Whole-Home Video", desc: "4K HDR video distribution from one centralized rack to every TV in the house. No clutter, no extra remotes.", to: "/services/whole-home-video" as const, link: true, image: wholeHomeVideoImg },
  { icon: Sun, title: "Outdoor Entertainment", desc: "Weather-rated 4K TVs, landscape speakers, pergola lighting, and full patio entertainment systems built for Colorado weather.", to: "/services/outdoor" as const, link: true, image: outdoorImg },
  { icon: Thermometer, title: "Smart Climate Control", desc: "Smart thermostats, multi-zone HVAC, and humidity control — integrated with lighting, shades, and your daily schedule.", to: "/services/climate-control" as const, link: true, image: climateImg },
  { icon: Lightbulb, title: "Smart Lighting", desc: "Architectural lighting design with tunable white and full-color circadian scenes — custom keypads in any finish.", to: "/services/lighting" as const, link: true, image: lightingImg },
  { icon: Blinds, title: "Motorized Shading", desc: "Whisper-quiet motorized shades that follow the Front Range sun — roller, roman, drapery, and cellular options.", to: "/services/shading" as const, link: true, image: shadingImg },
  { icon: Shield, title: "Security & Surveillance", desc: "4K camera systems, smart locks, video doorbells, and 24/7 monitored alarm integration.", to: "/services/security" as const, link: true, image: securityCardImg.url },
  { icon: Cpu, title: "Networking & Wi-Fi", desc: "Enterprise-grade networks engineered for streaming, gaming, and dozens of smart devices.", to: "/services/networking" as const, link: true, image: networkingImg.url },
  { icon: Building2, title: "Commercial AV", desc: "Commercial audio video for Denver businesses — meeting rooms, video walls, digital signage, zoned audio, and commercial networking.", to: "/services/commercial-av" as const, link: true, image: commercialAvImg },
  { icon: Speaker, title: "Conference & Office AV", desc: "Video conferencing rooms, video walls, and presentation systems for Denver businesses.", to: "/services/conference" as const, link: true, image: conferenceImg },
  { icon: HardHat, title: "New Construction Prewire", desc: "Low-voltage prewire during framing — structured cabling for AV, networking, security, lighting, and smart home, planned with your builder.", to: "/services/prewire" as const, link: true, image: prewireImg.url },
  { icon: Cable, title: "Retrofit & Cable Fishing", desc: "Add speakers, TVs, networking, and smart home wiring to finished homes — minimal access cuts, clean patching, no mess.", to: "/services/retrofit" as const, link: true, image: retrofitImg.url },
  { icon: Speaker, title: "AV Installation", desc: "Full-scope audio video installation in Denver — displays, in-ceiling speakers, equipment racks, video distribution, and one-app control for homes and businesses.", to: "/services/av-installation" as const, link: true, image: avInstallImg },
  { icon: Monitor, title: "TV Mounting", desc: "Flush, tilt, and full-motion TV mounts with hidden cables and in-wall power — drywall, brick, stone, or above the fireplace.", to: "/services/tv-mounting" as const, link: true, image: tvMountingImg },
  { icon: Mic, title: "Voice Control", desc: "Hands-free voice control for lights, shades, audio, climate, and security — Alexa, Google, Siri, and privacy-first local voice options.", to: "/services/voice-control" as const, link: true, image: voiceControlImg },
];

const servicesByTitle: Record<string, (typeof services)[number] | undefined> = Object.fromEntries(
  services.map((s) => [s.title, s])
);

const serviceGroups: { heading: string; blurb: string; titles: string[] }[] = [
  {
    heading: "Entertainment: home theater, audio & video",
    blurb:
      "Dedicated cinema rooms, multi-room music, and 4K video distribution for Denver homes — designed, calibrated, and supported by Apex AV.",
    titles: [
      "Custom Home Theater",
      "Dolby Atmos Home Theater",
      "Whole-Home Audio",
      "Hidden & Invisible Audio",
      "Whole-Home Video",
      "Outdoor Entertainment",
      "TV Mounting",
      "AV Installation",
    ],
  },
  {
    heading: "Control & comfort: smart home, lighting & shading",
    blurb:
      "One app and elegant keypads for lighting, shades, climate, security, and voice — tuned for Front Range sun and Colorado weather swings.",
    titles: [
      "Smart Home Automation",
      "Control4 Home Automation",
      "Smart Lighting",
      "Motorized Shading",
      "Smart Climate Control",
      "Voice Control",
      "Security & Surveillance",
    ],
  },
  {
    heading: "Infrastructure: networking, prewire & commercial AV",
    blurb:
      "The wiring and networks everything else depends on — new construction prewire, retrofit cable fishing, enterprise Wi-Fi, and office AV.",
    titles: [
      "Networking & Wi-Fi",
      "New Construction Prewire",
      "Retrofit & Cable Fishing",
      "Conference & Office AV",
    ],
  },
];



function ServicesPage() {
  return (
    <>
      <section className="container mx-auto px-6 pt-20 pb-12">
        <div className="max-w-3xl">
          <p className="text-sm font-semibold text-brand-text uppercase tracking-wider">What we do</p>
          <h1 className="mt-3 font-display text-5xl md:text-6xl font-bold">Services for every part of your home.</h1>
          <p className="mt-6 text-lg text-muted-foreground">
            From single-room audio upgrades to ground-up smart home builds, Apex AV designs and installs systems that perform — and stay supported for years.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-gradient-brand px-7 py-4 text-base font-semibold text-brand-foreground shadow-glow hover:opacity-90 transition"
            >
              Book a Free In-Home AV Consultation <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:+13033173233"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-card/60 backdrop-blur px-6 py-4 text-base font-semibold hover:bg-card transition"
            >
              Or call (303) 317-3233
            </a>
          </div>
        </div>
      </section>


      <section className="container mx-auto px-6 pb-12">
        <div className="max-w-3xl">
          <h2 className="font-display text-3xl md:text-4xl font-bold">Choosing where to start</h2>
          <p className="mt-4 text-muted-foreground">
            Most projects begin with one clear priority — a theater room that's finally getting built, speakers that need to reach the patio, or shades for west-facing windows that bake in the afternoon sun. Rather than trying to plan every system at once, we recommend picking the room or problem that bothers you most today and designing that properly. Because we run everything on the same network and control platform, later additions — audio in another room, lighting in the kitchen, a camera at the front door — join the existing system instead of creating a second app or a separate remote.
          </p>
          <h2 className="mt-10 font-display text-3xl md:text-4xl font-bold">How projects phase together</h2>
          <p className="mt-4 text-muted-foreground">
            Infrastructure work — networking, prewire, and equipment rack placement — comes first, since audio, video, lighting, and security all depend on it. From there, entertainment systems (theater, whole-home audio and video) and control systems (smart home, lighting, shading, climate) can be sequenced around your budget and calendar. A common pattern: prewire during construction or a remodel, core network and control at move-in, then theater or outdoor audio added once the rest of the house is settled.
          </p>
          <h2 className="mt-10 font-display text-3xl md:text-4xl font-bold">New build vs. retrofit</h2>
          <p className="mt-4 text-muted-foreground">
            New construction and major remodels give us open walls, so we run cabling for AV, networking, lighting, and security before drywall — the cleanest and most cost-effective time to wire a home. In finished homes, retrofit work uses attic and crawlspace access, wall-fishing techniques, and select access points to add wiring with minimal patching, or we lean on wireless options where running cable isn't practical. Either path lands on the same result: a system that looks intentional and works the same way room to room.
          </p>
        </div>
      </section>

      {serviceGroups.map((group) => (
        <section key={group.heading} className="container mx-auto px-6 py-12">
          <h2 className="font-display text-3xl md:text-4xl font-bold">{group.heading}</h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">{group.blurb}</p>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {group.titles.map((title) => {
              const s = servicesByTitle[title];
              if (!s) return null;
              return s.link ? (
                <Link
                  key={s.title}
                  to={s.to}
                  className="group rounded-xl border border-border bg-card shadow-card hover:border-brand/50 transition flex flex-col overflow-hidden"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                    <img
                      src={s.image}
                      alt={`${s.title} services in Denver, Cherry Creek, Highlands Ranch, Castle Rock and Castle Pines by Apex AV`}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card/90 via-card/20 to-transparent" />
                    <div className="absolute top-4 left-4 h-11 w-11 rounded-lg bg-gradient-brand flex items-center justify-center shadow-glow">
                      <s.icon className="h-5 w-5 text-brand-foreground" />
                    </div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="text-xl font-display font-bold">{s.title}</h3>
                    <p className="mt-3 text-muted-foreground text-sm flex-1">{s.desc}</p>
                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-text group-hover:gap-2 transition-all">
                      Explore {s.title} <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              ) : (
                <div key={s.title} className="rounded-xl border border-border bg-card p-8 shadow-card">
                  <div className="h-12 w-12 rounded-lg bg-gradient-accent flex items-center justify-center">
                    <s.icon className="h-6 w-6 text-accent-foreground" />
                  </div>
                  <h3 className="mt-6 text-xl font-display font-bold">{s.title}</h3>
                  <p className="mt-3 text-muted-foreground text-sm">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </section>
      ))}


      <section className="container mx-auto px-6 py-20">
        <div className="rounded-3xl bg-gradient-to-br from-card to-background border border-border p-10 md:p-14 text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold">Not sure where to start?</h2>
          <p className="mt-3 text-muted-foreground max-w-xl mx-auto">We'll walk your home, listen to your goals, and design a system that fits.</p>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow">
            Book a Free Consultation <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
